
import TextSelection from "./TextSelection";
import m, {Vnode} from "mithril";
import Token from "./Token";
import {Mark} from "../TextHighlighter";
import TextSelectionComment from "./TextSelectionComment";
import {Questionary} from "../../../model/questionary";

export default class TextContainer {
    private readonly tokens: Token[];
    private lastMarkedToken: Token|null = null;
    private dialog: TextSelectionComment|null = null;

    private rangeStart: Token|null = null;
    private rangeEnd: Token|null = null;

    constructor(
        text: string,
        public readonly questionary: Questionary,
        private readonly getMarker: ()=>Mark,
        private readonly onMarkToken: (token: Token) => void,
        private enableComments: boolean = true,
    ) {
        this.tokens = this.splitToTokens(text);
    }

    public getTokens(): Token[] {
        return this.tokens;
    }

    public markToken(token: Token, mark: Mark): void {
        if (token.type === 'word') {
            if (token.mark === mark) {
                this.lastMarkedToken = token;
                return;
            }
            token.selection = undefined;
            this.joinNeighbours(token.index); // clear on both sides

            this.lastMarkedToken = token;
            this.markSelection(token, mark); // mark and join selection
            this.joinNeighbours(token.index); // join if possible

            this.onMarkToken(token);
        }
    }
    
    private markRange(from: Token, to: Token, mark: Mark): void {
        let start = false, end = false;
        if (from.index > to.index) {
            [from, to] = [to, from];
        }
        this.tokens.forEach(token => {
            if (from === token) {
                start = true;
            }
            if (start && !end) {
                this.markToken(token, mark);   
            }
            if (to === token) {
                end = true;
            }
        });
    }

    private selectRange(from: Token, to: Token): void {
        this.resetRange();
        this.getRange(from, to).forEach(token => token.textSelect = true);
    }

    private getRange(from: Token, to: Token): Token[] {
        let start = false, end = false;
        if (from.index > to.index) {
            [from, to] = [to, from];
        }
        return this.tokens.filter(token => {
            let res = false;
            if (from === token) {
                start = true;
            }
            if (start && !end) {
                res = true;
            }
            if (to === token) {
                end = true;
            }
            return res;
        });
    }

    private resetRange(): void {
        this.rangeStart = null;
        this.rangeEnd = null;
        this.tokens.forEach(token => token.textSelect = false);
    }

    private markSelection(token: Token, mark: Mark) {
        if (mark === "none") {
            return;
        }
        const neighbourBefore = this.getNeighbour(token, "before", mark);
        const neighbourAfter = this.getNeighbour(token, "after", mark);
        if (neighbourBefore?.selection && !neighbourBefore.selection.comment && neighbourBefore.selection.mark === mark) {
            token.selection = neighbourBefore.selection;
        } else if (neighbourAfter?.selection && !neighbourAfter.selection.comment && neighbourAfter.selection.mark === mark) {
           token.selection = neighbourAfter.selection;
        } else {
           token.selection = new TextSelection(mark)
        }
    }

    private getNeighbour(token: Token, direction: "before"|"after", mark: Mark): Token|undefined {
        if (mark === "none") {
            return undefined;
        }
        const d = direction === "before" ? -1 : 1;
        let i = token.index;
        while (this.tokens[i]) {
            const t = this.tokens[i+d];
            if (!t || (t.type === "word" && t.mark !== mark)) {
                return undefined;
            } else if (t && t.type === "word" && t.mark === mark) {
                return t;
            }
            i = i + d;
        }
        return undefined;
    }

    public getComment(token: Token): string {
        return token.selection?.comment || '';
    }

    private splitToTokens(text: string): Token[] {
        text += ' '; // Because end last token
        const tokens: Token[] = [];
        let position = -1;

        const addSpace = (ch: string) => {
            tokens.push(new Token(
                tokens.length,
                position,
                'space',
                ch));
            };

        const isSpace = (ch: string) => /[\s​]/.test(ch);

        let token: Token|undefined;

        for (const char of text) {
            position++;
            if (!token) {
                let type: 'tag'|'word'|'space';
                if (char === '<') {
                    type = 'tag';
                } else if (isSpace(char)) {
                    addSpace(char)
                    continue;
                } else {
                    type = 'word';
                }
                token = new Token(
                    tokens.length,
                    position,
                    type,
                    char
                );
            } else if (token && token.type === "tag") {
                token.content += char;
                if (char === '>') {
                    tokens.push(token);
                    token = undefined;
                }
            } else if (token && token.type === "word") {
                if (char === '<') {
                    tokens.push(token)
                    token = new Token(
                        tokens.length,
                        position,
                        "tag",
                        char
                    );
                } else if (isSpace(char)) {
                    tokens.push(token)
                    addSpace(char)
                    token = undefined;
                } else {
                    token.content += char;
                }
            }
        }
        return tokens;
    }

    private shouldJoin(index: number, direction: 1|-1): number {
        let i = index;
        while (true) {
            i += direction;
            const token = this.tokens[i];
            if (!token) {
                return 0;
            }
            if (token.type === 'space' || token.type === 'tag') {
                token.selection = undefined;
                continue;
            }
            if (token.type === 'word') {
                return token.selection === this.tokens[index].selection ? (i - index)*direction : 0;
            }
        }
    }

    private joinNeighbours(index: number) {
        const shouldJoinUp = this.shouldJoin(index, 1);
        for (let i=0; i<shouldJoinUp; i++) {
            this.tokens[index+i].selection = this.tokens[index].selection;
        }

        const shouldJoinDown = this.shouldJoin(index, -1);
        for (let i=0; i<shouldJoinDown; i++) {
            this.tokens[index-i].selection = this.tokens[index].selection;
        }
    }

    private handleOnTokenClick(e: MouseEvent) {
        e.stopPropagation();
        if (e.target) {
            const $target = $(e.target);
            const token = this.getTokenFromEvent(e);

            let addComment = false;

            if (this.enableComments && $target.is('i.icon')) {
                addComment = true;
            }

            if (token) {
                this.dialog = null;
                const marker = this.getMarker();
                addComment = addComment || (marker !== "none" && token.mark === marker);

                this.markToken(token, marker);
                if (addComment) {
                    this.openDialog(token);
                }
            }
        }
    }

    private getTokenFromEvent(e: Event): Token|null {
        const $target = $(e.target!);
        const $token = $target.is('[data-index]') ? $target : $target.closest('[data-index]');
        const index = $token.data('index');
        return this.tokens[index] ?? null;
    }

    private handleOnStart(e: PointerEvent) {
        this.rangeStart = this.getTokenFromEvent(e);
    }

    private handleOnMove(e: PointerEvent) {
        if (this.rangeStart) {
            const rangeEnd = this.getTokenFromEvent(e);
            if (rangeEnd && this.rangeEnd !== rangeEnd) {
                this.rangeEnd = rangeEnd;
                this.selectRange(this.rangeStart, this.rangeEnd);
            }
        }
    }

    private handleOnStop() {
        if (this.rangeStart && this.rangeEnd) {
            this.markRange(this.rangeStart, this.rangeEnd, this.getMarker());
        }
        this.resetRange();
    }

    private handleOnCancel() {
        this.resetRange();
    }

    private openDialog(token: Token): void {
        if (!token.selection) {
            return;
        }
        this.lastMarkedToken = token;
        this.dialog = new TextSelectionComment(
            this.questionary,
            token.selection,
            () => {
                this.onMarkToken(token);
                this.dialog = null;
            },
        );
    }


    view(): Vnode {
        let selectableText = '';
        for (const i in this.tokens) {
            const token = this.tokens[i];
            selectableText += token.getHtml(
                this.enableComments && this.lastMarkedToken === token && token.mark !== "none",
                this.enableComments && Boolean(this.getComment(token))
            );
        }

        return m('.TextHighlighter-text', {
            onclick: this.handleOnTokenClick.bind(this),
            onpointerdown: this.handleOnStart.bind(this),
            onpointerup: this.handleOnStop.bind(this),
            onpointermove: this.handleOnMove.bind(this),
            onpointercancel: this.handleOnCancel.bind(this),
            onpointerleave: this.handleOnCancel.bind(this),
        }, [
            m.trust(selectableText),
            this.dialog ? m(this.dialog) : null,
        ]);
    }

}